/**
 * Quick Add: one line, straight into Todoist.
 *
 * The text goes to Todoist's own Quick Add parser, so it reads exactly as it
 * does in the Todoist app — "Call Sam tomorrow 3pm #Riverside p1" files, dates
 * and prioritises itself. Project Hub only adds a default destination: the
 * project (and section) you opened it from, used when the text names no
 * #project. The task reaches this app's mirror through a sync run right after,
 * so nothing here writes to PocketBase.
 *
 * Todoist's parser only files a #project whose full name is typed (spaces
 * escaped), so "#strat" would land in the title. Typing # or / here opens a
 * picker over the mirrored projects and sections instead; picking one sets
 * "Adds to" and takes the fragment out of the text, so the line Todoist parses
 * never carries a name it could misread. A name typed out in full and not
 * picked is still sent as typed, and Todoist files it.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent } from "react";
import type { Phase, Project } from "@/lib/types";
import { useProjects, useAllPhases } from "@/hooks/useData";
import { useToast } from "@/context/ToastContext";
import { quickAddTodoistTask, todoistUnavailableReason } from "@/lib/todoistClient";
import { SYNC_REQUEST_EVENT } from "@/hooks/useAutoSync";
import { parseDate, isToday } from "@/lib/format";
import { Dot } from "./atoms";

export interface QuickAddTarget {
  projectId?: string;
  phaseId?: string;
  /** YYYY-MM-DD, written into the text as a date Todoist will parse. */
  due?: string;
}

export function QuickAdd({ target, onClose }: { target: QuickAddTarget; onClose: () => void }) {
  const { data: projects = [] } = useProjects();
  const { data: phases = [] } = useAllPhases();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);

  // Where it goes: starts as where you opened it from, and the picker or the
  // chip's ✕ (null = the Inbox) changes it.
  const [dest, setDest] = useState<{ projectId: string; phaseId?: string } | null>(
    target.projectId ? { projectId: target.projectId, phaseId: target.phaseId } : null,
  );
  const project = dest ? projects.find((p) => p.id === dest.projectId) : undefined;
  const phase = dest?.phaseId ? phases.find((ph) => ph.id === dest.phaseId) : undefined;
  const sectionId = phase && realSection(phase) ? phase.todoistId : undefined;
  const into = project?.todoistId ? { projectId: project.todoistId, sectionId } : undefined;

  // Only what Todoist knows about can be picked: a project made by hand here
  // has no Todoist id to file into.
  const pickableProjects = useMemo(
    () => projects.filter((p) => p.todoistId && p.status !== "archived"),
    [projects],
  );
  const sectionsOf = (projectId?: string) =>
    phases.filter((ph) => ph.project === projectId && realSection(ph)).sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  const datePrefix = dueWords(target.due);
  const [text, setText] = useState(datePrefix ? ` ${datePrefix}` : "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const unavailable = todoistUnavailableReason();
  const [caret, setCaret] = useState(0);
  const [highlight, setHighlight] = useState(0);
  const list = useRef<HTMLDivElement>(null);
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);

  // The # or / fragment the caret is in, if any. Must start a word, so "10/15"
  // and the "//" before a description never open it.
  const fragment = useMemo(() => {
    const m = /(^|\s)([#/])([^\s#/]*)$/.exec(text.slice(0, caret));
    if (!m) return null;
    return { kind: m[2] as "#" | "/", query: m[3], start: m.index + m[1].length, end: caret };
  }, [text, caret]);
  // Esc hides the list for the fragment it was on; once that fragment is gone
  // (deleted, or the cursor left it), the next # or / opens it again.
  useEffect(() => {
    if (dismissedAt !== null && fragment?.start !== dismissedAt) setDismissedAt(null);
  }, [fragment, dismissedAt]);
  const token = fragment && fragment.start !== dismissedAt ? fragment : null;

  const options: { id: string; label: string; color: string; pick: () => void }[] = useMemo(() => {
    if (!token) return [];
    if (token.kind === "#") {
      return rank(pickableProjects, token.query, (p) => p.name).map((p: Project) => ({
        id: p.id, label: p.name, color: p.color,
        pick: () => setDest({ projectId: p.id }),
      }));
    }
    // Sections belong to the project already chosen; with none, / is just text.
    if (!project) return [];
    return rank(sectionsOf(project.id), token.query, (ph) => ph.name).map((ph: Phase) => ({
      id: ph.id, label: ph.name, color: ph.color || project.color,
      pick: () => setDest({ projectId: project.id, phaseId: ph.id }),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token?.kind, token?.query, pickableProjects, phases, project]);
  const pickerOpen = options.length > 0;

  useEffect(() => setHighlight(0), [token?.kind, token?.query]);

  // The list scrolls; keep the row the arrow keys are on in view.
  useEffect(() => {
    list.current?.children[highlight]?.scrollIntoView({ block: "nearest" });
  }, [highlight]);

  function choose(i: number) {
    if (!token) return;
    options[i].pick();
    // Take the fragment out; the chip says where it goes now.
    const before = text.slice(0, token.start).replace(/\s+$/, "");
    const after = text.slice(token.end).replace(/^\s+/, "");
    const next = before && after ? `${before} ${after}` : before + after;
    const at = before && after ? before.length + 1 : before.length;
    setText(next);
    setCaret(at);
    setDismissedAt(null);
    requestAnimationFrame(() => input.current?.setSelectionRange(at, at));
  }

  function onInputKey(e: ReactKeyboardEvent<HTMLInputElement>) {
    if (!pickerOpen || !token) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const n = options.length;
      setHighlight((h) => (h + (e.key === "ArrowDown" ? 1 : n - 1)) % n);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      choose(highlight);
    } else if (e.key === "Escape") {
      // Close the list, not the dialog — and keep it closed for this fragment.
      e.preventDefault();
      e.stopPropagation();
      setDismissedAt(token.start);
    }
  }

  const syncCaret = () => setCaret(input.current?.selectionStart ?? 0);

  // Cursor before a prefilled date, so you type the title and the date follows.
  useEffect(() => {
    const el = input.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(0, 0);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function submit() {
    const line = text.trim();
    if (!line || saving || unavailable) return;
    setSaving(true);
    setError(null);
    try {
      const res = await quickAddTodoistTask(line, into);
      toast(`Added “${res.content}”${res.due ? ` · ${res.due}` : ""}`);
      window.dispatchEvent(new Event(SYNC_REQUEST_EVENT));
      // Stay open for the next one, as Todoist does. Esc closes.
      setText(datePrefix ? ` ${datePrefix}` : "");
      requestAnimationFrame(() => input.current?.setSelectionRange(0, 0));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
      input.current?.focus();
    }
  }

  const destination = into
    ? <><Dot color={project!.color} size={7} />{project!.name}{phase && sectionId ? ` · ${phase.name}` : ""}</>
    : "Todoist Inbox";

  return (
    <div onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} style={scrim}>
      <div role="dialog" aria-label="Quick add" style={panel}>
        <form onSubmit={(e) => { e.preventDefault(); void submit(); }}>
          <input
            ref={input}
            value={text}
            onChange={(e) => { setText(e.target.value); setCaret(e.target.selectionStart ?? 0); }}
            onKeyDown={onInputKey}
            onKeyUp={syncCaret}
            onClick={syncCaret}
            placeholder="Call Sam tomorrow 3pm #Project p1"
            aria-label="Task"
            aria-autocomplete="list"
            aria-expanded={pickerOpen}
            disabled={!!unavailable}
            style={field}
          />

          {pickerOpen && (
            <div ref={list} role="listbox" aria-label={token?.kind === "#" ? "Projects" : "Sections"} style={picker}>
              {options.map((o, i) => (
                <div
                  key={o.id}
                  role="option"
                  aria-selected={i === highlight}
                  // mousedown, not click: keeps the cursor in the field.
                  onMouseDown={(e) => { e.preventDefault(); choose(i); }}
                  onMouseEnter={() => setHighlight(i)}
                  style={{ ...pickerRow, background: i === highlight ? "var(--accentTint)" : "transparent" }}
                >
                  <Dot color={o.color} size={7} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.label}</span>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
            <span style={{ fontSize: 12, color: "var(--muted)" }}>Adds to</span>
            <span style={chip}>
              {destination}
              {into && (
                <button type="button" onClick={() => setDest(null)} title="Send to the Inbox instead" aria-label="Send to the Inbox instead" style={chipX}>✕</button>
              )}
            </span>
            {project && !project.todoistId && (
              <span style={{ fontSize: 11.5, color: "var(--muted2)" }}>{project.name} isn't in Todoist</span>
            )}
            <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <button type="button" onClick={onClose} style={ghostBtn}>Cancel</button>
              <button type="submit" disabled={!text.trim() || saving || !!unavailable} style={{ ...primaryBtn, opacity: !text.trim() || saving || unavailable ? 0.5 : 1 }}>
                {saving ? "Adding…" : "Add task"}
              </button>
            </span>
          </div>

          {(error || unavailable) && (
            <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--bad)" }}>{error || unavailable}</div>
          )}

          <div className="mono" style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--tint2)", fontSize: 11, color: "var(--muted2)", lineHeight: 1.6 }}>
            tomorrow 3pm · #project and /section pick from a list · @label · p1 · {"{deadline}"} · // description
          </div>
        </form>
      </div>
    </div>
  );
}

/** A Todoist section, not the catch-all phase for tasks filed under the
 *  project itself ("nosection:<id>"), which has nothing to move into. */
function realSection(ph: Phase): boolean {
  return !!ph.todoistId && !ph.todoistId.startsWith("nosection:");
}

/** Name starts with the query, then a word in it does, then it contains it.
 *  Every match is kept — a bare # lists every project — and the list scrolls. */
function rank<T>(list: T[], query: string, name: (t: T) => string): T[] {
  const q = query.toLowerCase();
  const score = (t: T) => {
    const n = name(t).toLowerCase();
    if (n.startsWith(q)) return 0;
    if (n.split(/[\s\-–—·/]+/).some((w) => w.startsWith(q))) return 1;
    return n.includes(q) ? 2 : -1;
  };
  return list
    .map((t) => ({ t, s: score(t) }))
    .filter((x) => x.s >= 0)
    .sort((a, b) => a.s - b.s || name(a.t).localeCompare(name(b.t)))
    .map((x) => x.t);
}

/** A date as words Todoist parses: "today", else "Oct 9" (with the year when it isn't this one). */
function dueWords(due?: string): string {
  const d = parseDate(due);
  if (!d) return "";
  if (isToday(due)) return "today";
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString("en-US", sameYear ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" });
}

const scrim: CSSProperties = {
  position: "fixed", inset: 0, zIndex: 60, background: "var(--scrim)",
  display: "flex", justifyContent: "center", alignItems: "flex-start", padding: "12vh 16px 16px",
};

const panel: CSSProperties = {
  width: "min(620px, 100%)", boxSizing: "border-box", padding: "16px 16px 12px",
  background: "var(--raised)", border: "2px solid var(--accent)", borderRadius: 14,
  boxShadow: "var(--shadowOverlay)",
};

const field: CSSProperties = {
  width: "100%", boxSizing: "border-box", border: "none", outline: "none", background: "transparent",
  fontSize: 16, fontFamily: "inherit", color: "var(--ink)", padding: "4px 2px",
};

const picker: CSSProperties = {
  marginTop: 6, padding: 4, borderRadius: 10, border: "1px solid var(--line2)",
  background: "var(--surface)", maxHeight: 260, overflowY: "auto",
};

const pickerRow: CSSProperties = {
  display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderRadius: 7,
  fontSize: 13, color: "var(--ink2)", cursor: "pointer",
};

const chip: CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 6px 3px 10px", borderRadius: 999,
  border: "1px solid var(--line2)", background: "var(--surface)", fontSize: 12, color: "var(--ink2)",
};

const chipX: CSSProperties = {
  border: "none", background: "none", color: "var(--muted2)", cursor: "pointer", fontSize: 11, padding: "0 4px", lineHeight: 1,
};

const ghostBtn: CSSProperties = {
  padding: "7px 14px", borderRadius: 999, border: "1px solid var(--line2)", background: "var(--surface)",
  color: "var(--ink2)", fontSize: 12.5, cursor: "pointer",
};

const primaryBtn: CSSProperties = {
  padding: "7px 16px", borderRadius: 999, border: "none", background: "var(--accent)",
  color: "var(--onAccent)", fontSize: 12.5, fontWeight: 500, cursor: "pointer",
};
